import React from "react"
import AdSlotWithDevSupport from "./ad-slot-with-dev-support"

// Google Ad Manager environment statuses:
const GAM_ENV = {
  development: "development",
  production: "production",
  samples: "samples",
  justConsent: "justConsent",
  disabled: "disabled",
}

const gamConfiguration = () => {
  return process.env.GATSBY_AD_MANAGER_CONFIGURATION
    ? JSON.parse(process.env.GATSBY_AD_MANAGER_CONFIGURATION)
    : false
}

const gamAdUnitByName = adUnitName => {
  const adUnits = gamConfiguration()?.inventory?.ad_units
  return adUnits && adUnits[adUnitName] ? adUnits[adUnitName] : false
}

/**
 *
 * @param {Object} process.env object.
 * @returns The Google Ad Manager environment string variable from
 * the following values:
 *
 * GAM_ENV.development
 *
 * GAM_ENV.production
 *
 * GAM_ENV.samples
 *
 * GAM_ENV.disabled
 */
const gamEnvironment = () => {
  const currentEnv = process.env.GATSBY_AD_MANAGER_ENV
  const currentConf = gamConfiguration()
  if (currentEnv in GAM_ENV) {
    if (currentEnv === GAM_ENV.justConsent) {
      return currentEnv
    }
    if (currentConf && "network_code" in currentConf) {
      return currentEnv
    }
  }
  return GAM_ENV.disabled
}

const gamIsDev = () => {
  return gamEnvironment() === GAM_ENV.development
}

const gamIsSamples = () => {
  return gamEnvironment() === GAM_ENV.samples
}

const gamIsProduction = () => {
  return gamEnvironment() === GAM_ENV.production
}

const gamIsJustConsent = () => {
  return gamEnvironment() === GAM_ENV.justConsent
}

const gamIsDisabled = () => {
  return gamEnvironment() === GAM_ENV.disabled
}

/**
 *
 * @param {Object} process.env object.
 * @returns {boolean} true if a valid Google Ad Manager environment
 * is found. False otherwise.
 * */
const gamActive = () => {
  return gamEnvironment() !== GAM_ENV.disabled
}

const gamNetworkCode = () => {
  if (gamEnvironment() === GAM_ENV.samples) {
    return "6355419"
  }
  // https://developers.google.com/publisher-tag/guides/get-started
  if (gamEnvironment() === GAM_ENV.production) {
    const conf = gamConfiguration()
    return conf?.network_code ? conf.network_code : false
  }
  return false
}

function countWords(str) {
  let matches = str.match(/[\w\d\’\'-]+/gi)
  return matches ? matches.length : 0
}

const textContent = elem => {
  if (!elem) {
    return ""
  }
  if (typeof elem === "string") {
    return elem
  }
  /* Debugging for basic content shows that props.children, if any, is either a
     ReactElement, or a string, or an Array with any combination. Like for
     `<p>Hello <em>world</em>!</p>`:
     
       $$typeof: Symbol(react.element)
       type: "p"
       props:
         children:
           - "Hello "
           - $$typeof: Symbol(react.element)
             type: "em"
             props:
               children: "world"
           - "!" */
  const children = elem.props && elem.props.children
  if (Array.isArray(children)) {
    return children.map(textContent).join("")
  }
  return textContent(children)
}

const contentInjector = (
  adUnitConfiguration,
  nodes,
  rules = {
    /* Note that the ad will be displayed at the end, and not 
      in the middle of an html node. */
    /* nWordsBeforeFirstAd:
       The minimum number of words before the first ad will show. 
       */
    nWordsBeforeFirstAd: 100,
    /* nWordsBetweenAds:
      The minimum number of words between ads.
    */
    nWordsBetweenAds: 200,
  }
) => {
  const { nWordsBeforeFirstAd, nWordsBetweenAds } = rules
  if (!adUnitConfiguration) {
    return nodes
  }
  const outputNodes = []
  for (const node of nodes) {
    outputNodes.push(node)
  }
  let wordsAfterLastAd = 0
  let currentNodeIndex = 0
  let firstAdInserted = false
  for (const node of nodes) {
    wordsAfterLastAd += countWords(textContent(node))
    if (
      (wordsAfterLastAd >= nWordsBeforeFirstAd && !firstAdInserted) ||
      (wordsAfterLastAd >= nWordsBetweenAds && firstAdInserted)
    ) {
      outputNodes.splice(
        currentNodeIndex + (outputNodes.length - nodes.length) + 1,
        0,
        <AdSlotWithDevSupport
          // https://react-dfp.surge.sh/examples/basic/
          className="ad-slot-wpc"
          adSlotId={`ad-slot-wpc-${currentNodeIndex + 1}`}
          key={`ad-slot-${currentNodeIndex + 1}`}
          adUnitConfiguration={adUnitConfiguration}
        ></AdSlotWithDevSupport>
      )
      firstAdInserted = true
      wordsAfterLastAd = 0
    }
    currentNodeIndex += 1
  }
  return outputNodes
}

const compareResponsiveSizes = (a, b) => {
  // const x = ""
  if (a.screen.width < b.screen.width) {
    return -1
  }
  if (a.screen.width > b.screen.width) {
    return 1
  }
  // Equal widths, let's check the heights
  if (a.screen.height < b.screen.height) {
    return -1
  }
  if (a.screen.height > b.screen.height) {
    return 1
  }
  return 0
}

export {
  gamActive,
  gamAdUnitByName,
  gamIsDev,
  gamIsJustConsent,
  gamIsSamples,
  gamIsProduction,
  gamIsDisabled,
  gamEnvironment,
  gamNetworkCode,
  gamConfiguration,
  contentInjector,
  compareResponsiveSizes,
}
